import * as THREE from 'three';

import { ImagePresentation } from '@/engine/UIPresentation/ImagePresentation';
import { Image3dPresentationPlaneStrategy } from '@/engine/UIPresentation/PresentationVisibilityStrategies';
import { PointerHandlerAdjustCamera } from './PointerHandlerAdjustCamera';
import { HumanPresentation, SceneCorner } from '@/engine/UIPresentation/HumanPresentation';
import { PointerHandlerSynchronizeImages } from '@/engine/app/modules/images/PointerHandlerSynchronizeImages';
import { GhostCursorPresentation } from '@/engine/UIPresentation/GhostCursorPresentation';
import { CameraInitializer } from './CameraInitializer';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { ViewPortOrientation } from '@/engine/app/models/ViewPortOrientation';

import type { ViewsService } from '@/engine/app/Views.service';
import type { IThreeDViewPortDetails } from '@/engine/app/models/viewEntity/view.entity.ts';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { IDisposable } from '@/engine/Utilities/IDisposable';
import type { IPointerController } from './PointerController';
import type { IImagePresentationPlaneStrategy } from '@/engine/UIPresentation/PresentationVisibilityStrategies';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';
import type {
  IPresentationChangeHandler,
  IUiThreeDPresentation,
  IUiThreeDPresentationContext,
  IUiTwoDPresentationContext
} from '@/engine/UIPresentation/Interfaces';

import type {
  ICameraInfoApi,
  ICameraInitializer,
  ICenterLineViewModel,
  IImageSliceViewModel,
  IMeasurementViewModel,
  IObjectViewModel,
  IThreeDPresentationFactory,
  IRaycasterProvider,
  IViewPortControllerStrategy,
  IViewPortController,
  IViewPortManagerStrategy,
  Location,
  ICameraController,
  IPickable
} from '@/engine/app/interfaces';
import { ViewPortKind } from '@/engine/app/interfaces';
import type { ViewController } from '@/engine/app/View.controller';

export class ViewPort3dStrategy implements IViewPortManagerStrategy, IViewPortControllerStrategy {
  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _imagesService: ImagesService;

  private readonly _viewsService: ViewsService;

  private readonly _viewController: ViewController;

  private readonly _viewportController: IViewPortController;

  private readonly _viewDetails: IThreeDViewPortDetails;

  private _subscriptions: DisposablesBasket = new DisposablesBasket();

  private _presentationFactory: IThreeDPresentationFactory;

  private _raycasterProvider?: IRaycasterProvider;

  private _pointerHandlerAdjustCamera?: PointerHandlerAdjustCamera;

  public constructor(
    viewportController: IViewPortController,
    planningAlgorithm: PlanningAlgorithm,
    imagesService: ImagesService,
    viewsService: ViewsService,
    viewController: ViewController,
    viewDetails: IThreeDViewPortDetails,
    presentationFactory: IThreeDPresentationFactory
  ) {
    this._viewportController = viewportController;
    this._planningAlgorithm = planningAlgorithm;
    this._imagesService = imagesService;
    this._viewsService = viewsService;
    this._viewController = viewController;
    this._viewDetails = viewDetails;
    this._presentationFactory = presentationFactory;

    this._subscriptions.add(
      this._viewportController.getCameraController().onCameraModified((cameraInfo: ICameraInfoApi): void => {
        this._viewDetails.setOrientation(
          new ViewPortOrientation(cameraInfo.getCameraTop(), cameraInfo.getCameraDirection())
        );
      })
    );
  }

  public setRaycasterProvider(raycasterProvider: IRaycasterProvider): void {
    this._raycasterProvider = raycasterProvider;
    this._presentationFactory.setRaycasterProvider(raycasterProvider);
  }

  public getDefaultPickables(): Array<IPickable> | undefined {
    return undefined;
  }

  public getViewPortControllerStrategy(): IViewPortControllerStrategy {
    return this;
  }

  // IViewPortControllerStrategy
  public setViewRoi(location: Location, zoomFactor: number): void {
    this._viewportController.getCameraController().setCameraOrientation({
      topVector: location.dirY.clone().negate(),
      directionVector: location.dirZ.clone().negate()
    });
    this._viewportController
      .getCameraController()
      .fitCameraToPoints([location.minCorner, location.maxCorner], { fittingFactor: zoomFactor });
  }

  // IViewPortControllerStrategy
  public synchronizeCamera(anotherCamera: ICameraController): void {
    this._viewportController.getCameraController().synchronizeWith(anotherCamera);
  }

  // IViewPortControllerStrategy
  public setNoRotateCamera(noRotate: boolean): void {
    if (!this._pointerHandlerAdjustCamera) {
      throw new EvalError('PointerHandlerAdjustCamera must be set at this point');
    }
    this._pointerHandlerAdjustCamera.setNoRotate(noRotate);
  }

  public createDefaultCameraInitializer(): ICameraInitializer {
    return new CameraInitializer(
      this._viewportController,
      this._viewsService,
      this._planningAlgorithm,
      this._viewDetails
    );
  }

  public createKeyboardHandlers(): void {
    void 0;
  }

  public createPointerHandlers(pointerController: IPointerController): void {
    if (!this._raycasterProvider) {
      throw new EvalError('Raycaster provider must be set at this point');
    }

    this._pointerHandlerAdjustCamera = new PointerHandlerAdjustCamera(
      this._viewportController,
      this._raycasterProvider.getRaycaster(),
      false,
      false
    );

    pointerController.pushPointerHandler(this._pointerHandlerAdjustCamera);

    pointerController.pushPointerHandler(
      new PointerHandlerSynchronizeImages(
        this._imagesService,
        this._raycasterProvider.getRaycaster(),
        this._viewController
      )
    );
  }

  public createCenterLinePresentation(
    viewModel: ICenterLineViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext {
    return this._presentationFactory.createCenterLinePresentation(viewModel, changeHandler);
  }

  public createThreeDObjectPresentation(
    viewModel: IObjectViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._presentationFactory.createThreeDObjectPresentation(viewModel, changeHandler);
  }

  public createThreeDMeasurementPresentation(
    viewModel: IMeasurementViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext | undefined {
    return this._presentationFactory.createThreeDMeasurementPresentation(viewModel, changeHandler);
  }

  public createTwoDObjectPresentation(viewModel: IObjectViewModel): IUiTwoDPresentationContext | undefined {
    return this._presentationFactory.createTwoDObjectPresentation(viewModel);
  }

  public createMeasurementLabel(viewModel: IMeasurementViewModel): IUiTwoDPresentationContext | undefined {
    return this._presentationFactory.createTwoDMeasurementLabelPresentation(viewModel);
  }

  public createMeasurementTwoDPresentation(viewModel: IMeasurementViewModel): IUiTwoDPresentationContext | undefined {
    return this._presentationFactory.createTwoDMeasurementPresentation(viewModel);
  }

  public createDependencyController(): IDisposable | undefined {
    return;
  }

  public createImagePresentation(
    imageModel: IImageSliceViewModel,
    changeHandler: IPresentationChangeHandler
  ): IUiThreeDPresentationContext {
    const planeStrategy: IImagePresentationPlaneStrategy = new Image3dPresentationPlaneStrategy();
    const imagePresentation = new ImagePresentation(this._imagesService, imageModel, changeHandler, planeStrategy);
    return {
      presentation: imagePresentation,
      raycastable: imagePresentation
    };
  }

  public createViewHelpersPresentations(changeHandler: IPresentationChangeHandler): Array<IUiThreeDPresentation> {
    const presentations: Array<IUiThreeDPresentation> = [];
    presentations.push(
      new HumanPresentation(
        SceneCorner.LeftBottom,
        27,
        50,
        new THREE.Vector2(5, 20),
        new THREE.Vector2(15, 30),
        changeHandler,
        this._viewportController,
        new THREE.Color(0xffffff),
        0.3
      )
    );

    presentations.push(new GhostCursorPresentation(changeHandler, this._viewportController, this._viewsService));
    return presentations;
  }

  public resetView(): void {
    this._viewController.resetView(ViewPortKind.ThreeD);
  }

  public dispose(): void {
    this._subscriptions.dispose();
    this._pointerHandlerAdjustCamera = undefined;
  }
}
